<template>
	<div>
		<ui-modal :show="show" @update:show="updateShow($event)">
			<ui-panel>
				<ui-panel-title><div class="panel-heading">Create Guest</div></ui-panel-title>
				<ui-panel-body>
					<ui-form ref="form">
						<div class="row">
							<div class="col-md-6">
								<ui-text-box-field
									label="First Name"
									:rules="rules.first_name"
									v-model="guest.first_name">
								</ui-text-box-field>
							</div>
							<div class="col-md-6">
								<ui-text-box-field
									label="Last Name"
									:rules="rules.last_name"
									v-model="guest.last_name">
								</ui-text-box-field>
							</div>
							<div class="col-md-6">
								<ui-text-box-field
									label="Email"
									:rules="rules.email"
									v-model="guest.primary_email"
									@input="onEmailInput">
								</ui-text-box-field>
								<div v-if="emailLookupCount !== null && emailLookupCount > 0" class="mt-1" style="font-size: 12px; color: #666;">
									{{ emailLookupCount }} active guest(s) found with this email
								</div>
							</div>
							<div class="col-md-6">
								<ui-select-field
									label="Gender"
									:items="genderOptions"
									:rules="rules.gender"
									v-model="guest.gender">
								</ui-select-field>
							</div>
						</div>
					</ui-form>
				</ui-panel-body>
				<ui-panel-footer>
					<ui-button color="primary" :loading="saving" :disabled="!canSave" @click="save">Save</ui-button>
					<ui-button color="text" :disabled="saving" @click="dismiss(false)">Close</ui-button>
				</ui-panel-footer>
			</ui-panel>
		</ui-modal>

		<guest-selection-modal
			:show="showSelectionModal"
			:email="guest.primary_email"
			:guests="existingGuests"
			@selected="useExistingGuest"
			@cancel="cancelSelection"
			@update:show="showSelectionModal = $event" />
	</div>
</template>

<script>
import validate from 'igolf/shared/validators';
import GuestSelectionModal from './GuestSelectionModal.vue';

const defaultGuest = { first_name: '', last_name: '', primary_email: '', gender: '' };

import alerts from 'igolf/shared/alerts';

export default {
	components: {
		GuestSelectionModal,
	},

	props: {
		show: { type: Boolean, default: false },
	},

	data()
	{
		return {
			saving: false,
			guest: { ...defaultGuest },
			showSelectionModal: false,
			existingGuests: [],
			checkingEmail: false,
			emailLookupCount: null,
			emailLookupTimer: null,

			genderOptions: [
				{ text: '(select gender)', value: '' },
				{ text: 'Male', value: 'male' },
				{ text: 'Female', value: 'female' },
				//{ text: 'Other', value: 'other' },
			],

			rules: {
				first_name: [ v => !!v || 'Please enter the guest\'s first name' ],
				last_name: [ v => !!v || 'Please enter the guest\'s last name' ],
				gender: [ v => !!v || 'Please select the guest\'s gender' ],
				email: [ 
					v => !!v || 'Please enter the guest\'s email address',
					v => validate.email(v) || 'Invalid email address',
				],
			}
		}
	},

	computed: {
		canSave()
		{
			return this.guest.first_name && this.guest.last_name && this.guest.primary_email && validate.email(this.guest.primary_email) && this.guest.gender;
		}
	},

	methods: {
		// tell the container to hide the popup
		dismiss(refresh)
		{
			this.guest = { ...defaultGuest };
			this.existingGuests = [];
			this.showSelectionModal = false;
			this.emailLookupCount = null;
			if (this.emailLookupTimer) {
				clearTimeout(this.emailLookupTimer);
				this.emailLookupTimer = null;
			}
			this.$emit('close', refresh);
		},

		// if the user clicked the built-in closed button or triggered an out-side click
		updateShow(flag)
		{
			// if flag is false then dismiss the popup
			if (!flag)
				this.dismiss(false);
		},

		onEmailInput()
		{
			if (this.emailLookupTimer) {
				clearTimeout(this.emailLookupTimer);
			}
			this.emailLookupTimer = setTimeout(() => {
				this.lookupEmailCount();
			}, 500);
		},

		async lookupEmailCount()
		{
			// Only check if email is valid
			if (!this.guest.primary_email || !validate.email(this.guest.primary_email))
			{
				this.emailLookupCount = null;
				return;
			}

			try
			{
				const result = await this.$api.post('/users/find_guests_by_email', { 
					email: this.guest.primary_email 
				});
				
				this.emailLookupCount = result.data.count || 0;
			}
			catch(err)
			{
				// Silently fail - just don't show count
				this.emailLookupCount = null;
			}
		},

		async checkEmailExists()
		{
			// Only check if email is valid and present
			if (!this.guest.primary_email || !validate.email(this.guest.primary_email))
			{
				this.emailLookupCount = null;
				return;
			}

			this.checkingEmail = true;

			try
			{
				var result = await this.$api.post('/users/find_guests_by_email', { 
					email: this.guest.primary_email 
				});
				
				this.emailLookupCount = result.data.count || 0;
				
				if (result.data.guests && result.data.guests.length > 0)
				{
					this.existingGuests = result.data.guests;
					this.showSelectionModal = true;
				}
			}
			catch(err)
			{
				// Silently fail - user can still try to save
				console.error('Error checking email:', err);
				this.emailLookupCount = null;
			}
			finally
			{
				this.checkingEmail = false;
			}
		},

		async checkExisting()
		{
			var result = await this.$api.post('/guests/check_existing', { guest: this.guest });
			return result.data.count > 0;
		},

		useExistingGuest(selectedGuest)
		{
			// Use the selected existing guest
			this.showSelectionModal = false;
			this.$emit('guest-selected', selectedGuest);
			this.dismiss(true);
		},

		cancelSelection()
		{
			this.showSelectionModal = false;
			// User can continue editing or change email
		},

		async save()
		{
			if (!this.$refs.form.validate())
				return;

			this.saving = true;

			try
			{
				// Try to create guest
				var result = await this.$api.post('/guests', { guest: this.guest });
				
				// If successful, dismiss
				this.dismiss(true);
			}
			catch(err)
			{
				if (err.response && err.response.status === 422)
				{
					const data = err.response.data || {};
					
					// Check if it's a duplicate email error - try to fetch existing guests
					if (data.error_fields?.primary_email || (data.errors && Array.isArray(data.errors) && data.errors.some(e => e.toLowerCase().includes('email') || e.toLowerCase().includes('taken'))))
					{
						try
						{
							const checkResult = await this.$api.post('/users/find_guests_by_email', { 
								email: this.guest.primary_email 
							});
							
							if (checkResult.data.guests && checkResult.data.guests.length > 0)
							{
								this.existingGuests = checkResult.data.guests;
								this.showSelectionModal = true;
								return;
							}
						}
						catch(checkErr)
						{
							console.error('Error checking for existing guests:', checkErr);
						}
					}
					
					// Extract error message
					const errorMessage = data.error || 
						(Array.isArray(data.errors) ? data.errors.join(', ') : data.errors) ||
						(data.error_fields && Object.entries(data.error_fields).map(([field, msgs]) => 
							`${field === 'primary_email' ? 'Email' : field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`
						).join(', ')) ||
						'Please check the form and try again.';
					
					await alerts.show(errorMessage, 'Error', 'error');
				}
				else
				{
					const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to create guest';
					await alerts.show(errorMsg, 'Error', 'error');
				}
			}
			finally
			{
				this.saving = false;
			}
		}
	}
}
</script>
